import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, User, FileText, DollarSign, Tag, Loader2, ExternalLink, Building, Map, ChevronRight, Home, Info } from 'lucide-react';
import authFetch from '../../utils/authFetch';

// HARDCODED FIX: Smartly switch between Localhost and Secure Server
const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

const TransactionDetailModal = ({ item, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Responsive State
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isZK = item.source === 'ZK';
  const isRental = item.source === 'ETN_RENTAL';
  
  // --- FETCH DATA ---
  useEffect(() => {
    const params = new URLSearchParams({
        source: item.source,
        db_id: item.db_id,
        property_type: item.property_type
    });

    authFetch(`${API_BASE}/api/details?${params.toString()}`)
        .then(res => res.json())
        .then(response => {
            if (response.type === 'deal') {
                setData(response);
            } else if (response.type === 'zk') {
                setData({ transaction: {}, properties: [], zk: response.data });
            }
            setLoading(false);
        })
        .catch(err => {
            console.error(err);
            setLoading(false);
        });
  }, [item]);

  // Derived State
  const properties = data?.properties || [];
  const transaction = data?.transaction || {};
  const currentProperty = properties[selectedIndex] || {};
  const zkData = data?.zk || {};

  // Merge for unified access
  const raw = isZK ? { ...item, ...zkData } : { ...transaction, ...currentProperty };
  const decoded = transaction._decoded || {}; // Access decoded values from backend

  // --- CONFIGURATION ---
  
  const transactionFields = [
    { label: "ID Posla", key: "id_posla" },
    { label: "Datum Sklenitve", key: "datum_sklenitve_pogodbe" },
    
    // Price
    { 
      label: isRental ? "Mesečna Najemnina" : "Pogodbena Cena", 
      keys: isRental ? ["pogodbena_najemnina", "najemnina"] : ["pogodbena_cena_odskodnina", "pogodbena_cena_dela_stavbe"], 
      isPrice: true 
    },
    
    // Decoded Fields (Code + Description)
    { label: "Vrsta Posla", keys: ["vrsta_kupoprodajnega_posla"], fullWidth: true, decodeKey: 'vrsta_kupoprodajnega_posla' },
    { label: "Vrsta Akta", keys: ["vrsta_akta", "vrsta_pravnega_posla"], decodeKey: 'vrsta_akta' },
    { label: "Tržnost", keys: ["trznost_posla", "trznost"], decodeKey: 'trznost_posla' },
    
    { label: "DDV Vključen", key: "vkljucenost_ddv" },
    { label: "Stopnja DDV", key: "stopnja_ddv" },
    { label: "Opombe", keys: ["opombe_o_pravnem_poslu", "opombe"], fullWidth: true, isLong: true },
  ];

  const buildingFields = [
    { label: "Leto Izgradnje", keys: ["leto_izgradnje_dela_stavbe", "leto"] },
    { label: "Nadstropje", keys: ["nadstropje_dela_stavbe"] },
    { label: "Št. Sob", keys: ["stevilo_sob"] },
    { label: "Uporabna Površina", keys: ["uporabna_povrsina"], suffix: " m²" },
    { label: "Površina Dela", keys: ["povrsina_dela_stavbe"], suffix: " m²" },
    { label: "Dejanska Raba", keys: ["dejanska_raba_dela_stavbe"] },
    { label: "Prostori", keys: ["prostori_dela_stavbe"], fullWidth: true, isList: true }, // NEW
  ];

  const landFields = [
    { label: "Površina Parcele", keys: ["povrsina_parcele"], suffix: " m²" },
    { label: "Namenska Raba", keys: ["namenska_raba"] },
    { label: "Boniteta", keys: ["boniteta_zemljisca"] },
  ];

  // --- HELPER: FORMAT VALUES ---
  const formatVal = (field) => {
    let val = null;

    // 1. Find value
    if (field.key) val = raw[field.key] || raw[field.key.replace('posla', '').trim()];
    if (!val && field.keys) {
        for (const k of field.keys) {
            if (raw[k] !== undefined && raw[k] !== null) { val = raw[k]; break; }
        }
    }

    if (val === null || val === undefined || val === '' || val === 'null') return '-';

    // 2. Formatting
    if (field.isPrice) {
        const num = parseFloat(val.toString().replace(/\./g, '').replace(',', '.'));
        return isNaN(num) ? val : num.toLocaleString('sl-SI', { style: 'currency', currency: 'EUR' });
    }
    
    // 3. Decoding (Code -> Description)
    if (field.decodeKey && decoded[field.decodeKey]) {
        return (
            <span>
                <span style={{fontWeight:'bold'}}>{val}</span>
                <span style={{color:'#64748b', marginLeft:'6px', fontStyle:'italic'}}>
                   - {decoded[field.decodeKey]}
                </span>
            </span>
        );
    }

    // 4. List Handling (Prostori)
    if (field.isList) {
        // Try parsing if it's a string looking like JSON
        let list = val;
        try { if (typeof val === 'string' && val.startsWith('[')) list = JSON.parse(val); } catch(e){}
        
        if (Array.isArray(list)) {
            return (
                <div style={{display:'flex', flexWrap:'wrap', gap:'5px'}}>
                    {list.map((it, i) => (
                        <span key={i} style={{background:'#f1f5f9', padding:'2px 6px', borderRadius:'4px', fontSize:'12px'}}>
                            {typeof it === 'object' ? `${it.prostor || '?'} (${it.povrsina || '?'} m²)` : it}
                        </span>
                    ))}
                </div>
            );
        }
    }

    if (field.suffix) return val + field.suffix;
    return val;
  };

  if (!item) return null;

  return (
    <div style={overlayStyle}>
      <div style={{
          ...modalStyle,
          width: '1000px', // Max width for desktop
          padding: isMobile ? '15px' : '30px', // Reduce padding on mobile
      }}>
        
        {/* HEADER */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', borderBottom: '1px solid #e2e8f0', paddingBottom: '15px', marginBottom: '20px' }}>
          <div style={{ paddingRight: '10px' }}>
            <h2 style={{ margin: 0, color: '#1e293b', fontSize: isMobile ? '18px' : '20px', display:'flex', alignItems:'center', gap:'10px', flexWrap: 'wrap' }}>
                {isZK ? <FileText size={24} color="#b45309"/> : (isRental ? <Tag size={24} color="#7c3aed"/> : <DollarSign size={24} color="#059669"/>)}
                {isZK ? 'Izpis iz Zemljiške knjige' : (isRental ? 'Najemni Posel' : 'Kupoprodajni Posel')}
            </h2>
            <div style={{ display: 'flex', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
                <span style={idBadgeStyle}>{raw.id_posla || item.id_sign || 'ID'}</span>
                {!isZK && raw.id_posla && (
                    <Link to={`/real-estate/raw/${isRental ? 'rentals' : 'sales'}/posli?column=id_posla&search=${raw.id_posla}`} target="_blank" style={linkButtonStyle}>
                        <ExternalLink size={14} /> View Raw
                    </Link>
                )}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', minWidth: '30px' }}><X size={28} /></button>
        </div>

        {loading ? (
            <div style={{ padding: '50px', textAlign: 'center', color: '#64748b' }}><Loader2 size={32} className="spin" /></div>
        ) : (
            <div style={{ 
                display: 'grid', 
                // CONDITIONAL GRID: Stack columns on mobile
                gridTemplateColumns: isMobile ? '1fr' : (isZK ? '1fr 1fr' : '300px 1fr'), 
                gap: '30px' 
            }}>
                
                {/* --- LEFT SIDE: SELECTION LIST --- */}
                {isZK ? (
                    <div>
                        <h3 style={sectionHeaderStyle}>Nepremičnina</h3>
                        <div style={gridContainerStyle}>
                            <Field label="KO" value={raw.ko} />
                            <Field label="Parcela/Stavba" value={raw.number} />
                            <Field label="Del" value={raw.part} />
                            <Field label="Lokacija" value={raw.cadastral_details} />
                        </div>
                    </div>
                ) : (
                    <div style={{ borderRight: isMobile ? 'none' : '1px solid #e2e8f0', borderBottom: isMobile ? '1px solid #e2e8f0' : 'none', paddingRight: isMobile ? 0 : '20px', paddingBottom: isMobile ? '20px' : 0 }}>
                        <h3 style={sectionHeaderStyle}>Vključene Nepremičnine ({properties.length})</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: isMobile ? '200px' : '500px', overflowY: 'auto' }}>
                            {properties.length === 0 && <div style={{color:'#94a3b8', fontStyle:'italic'}}>Ni povezanih nepremičnin.</div>}
                            {properties.map((prop, idx) => {
                                const isSelected = idx === selectedIndex;
                                const isLand = prop._type === 'parcela' || prop.vrsta_zemljisca;
                                const title = isLand ? `Parcela ${prop.parcelna_stevilka || ''}` : `Del stavbe ${prop.stevilka_dela_stavbe || ''}`;
                                const size = prop.povrsina_parcele || prop.povrsina_dela_stavbe || prop.uporabna_povrsina || '?';
                                const ko = prop.sifra_ko || prop.ime_ko || '';

                                return (
                                    <div 
                                        key={idx} 
                                        onClick={() => setSelectedIndex(idx)}
                                        style={{
                                            padding: '10px',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            background: isSelected ? '#eff6ff' : '#f8fafc',
                                            border: isSelected ? '1px solid #bfdbfe' : '1px solid #e2e8f0',
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                                        }}
                                    >
                                        <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                                            {isLand ? <Map size={16} color="#059669"/> : <Home size={16} color="#2563eb"/>}
                                            <div>
                                                <div style={{fontSize:'13px', fontWeight:'600', color:'#334155'}}>{title}</div>
                                                <div style={{fontSize:'11px', color:'#64748b'}}>{ko} | {size} m²</div>
                                            </div>
                                        </div>
                                        {isSelected && <ChevronRight size={16} color="#2563eb"/>}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* --- RIGHT SIDE: DETAILS --- */}
                <div>
                    {isZK ? (
                        <>
                            <h3 style={sectionHeaderStyle}>Lastniki</h3>
                            <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                {raw.owners?.map((o, i) => (
                                    <div key={i} style={{ marginBottom: '10px', borderBottom: '1px dashed #e2e8f0', paddingBottom:'10px' }}>
                                        <div style={{fontWeight:'600', color:'#334155', wordBreak: 'break-word'}}>{o.imetnik_ime || 'Unknown'}</div>
                                        <div style={{fontSize:'12px', color:'#64748b'}}>Delež: <b>{o.delez}</b></div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <>
                            <h3 style={sectionHeaderStyle}>Podatki o Transakciji</h3>
                            <div style={{...gridContainerStyle, marginBottom:'20px'}}>
                                {transactionFields.map((f, i) => (
                                    <div key={i} style={f.fullWidth ? { gridColumn: '1 / -1' } : {}}>
                                        <div style={labelStyle}>{f.label}</div>
                                        <div style={f.isLong ? longTextStyle : valueStyle}>{formatVal(f)}</div>
                                    </div>
                                ))}
                            </div>

                            <h3 style={{...sectionHeaderStyle, borderTop:'1px solid #f1f5f9', paddingTop:'15px'}}>
                                Podatki o Izbrani Nepremičnini
                            </h3>
                            <div style={gridContainerStyle}>
                                <Field label="KO" value={currentProperty.ime_ko || currentProperty.sifra_ko} />
                                <Field label="Naselje" value={currentProperty.naselje || currentProperty.obcina} />
                                <Field label="Ulica" value={currentProperty.ulica} />
                                <Field label="Hišna št." value={currentProperty.hisna_stevilka} />
                                
                                {(currentProperty.povrsina_parcele || currentProperty.vrsta_zemljisca ? landFields : buildingFields).map((f, i) => (
                                    <div key={i} style={f.fullWidth ? { gridColumn: '1 / -1' } : {}}>
                                        <div style={labelStyle}>{f.label}</div>
                                        <div style={f.isLong ? longTextStyle : valueStyle}>{formatVal(f)}</div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

            </div>
        )}
      </div>
    </div>
  );
};

// Styles
const Field = ({ label, value }) => (<div><div style={labelStyle}>{label}</div><div style={valueStyle}>{value || '-'}</div></div>);
const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, backdropFilter: 'blur(3px)' };
const modalStyle = { backgroundColor: 'white', borderRadius: '16px', maxWidth: '95%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.2)', boxSizing: 'border-box' };
const sectionHeaderStyle = { fontSize: '14px', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', borderBottom: '2px solid #f1f5f9', paddingBottom: '8px', marginBottom: '15px' };
const gridContainerStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px 20px' };
const labelStyle = { fontSize: '11px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' };
const valueStyle = { fontSize: '14px', color: '#1e293b', fontWeight: '500' };
const longTextStyle = { fontSize: '13px', color: '#334155', background: '#fffbeb', padding: '10px', borderRadius: '6px', border: '1px solid #fcd34d', lineHeight: '1.5' };
const idBadgeStyle = { fontFamily: 'monospace', background: '#f1f5f9', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', color: '#334155' };
const linkButtonStyle = { display: 'flex', alignItems: 'center', gap: '6px', background: '#eff6ff', color: '#2563eb', padding: '4px 10px', borderRadius: '4px', fontSize: '12px', textDecoration: 'none', fontWeight: '600', border: '1px solid #dbeafe' };

export default TransactionDetailModal;